import React, { useEffect, useState } from "react";
import { ClipboardCheck, Send, FileDown, RefreshCw, AlertTriangle, CheckCircle2, TrendingDown, History, Clock, Siren, CheckCheck } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_STYLES = {
  goals:          "bg-amber-50 text-amber-800 border-amber-200",
  attendance:     "bg-indigo-50 text-indigo-700 border-indigo-200",
  documentation:  "bg-orange-50 text-orange-700 border-orange-200",
  time:           "bg-purple-50 text-purple-700 border-purple-200",
};

const STATUS_STYLES = {
  pending:   { label: "Ausstehend", cls: "bg-slate-50 text-slate-600 border-slate-200", icon: Clock },
  escalated: { label: "Eskaliert",  cls: "bg-red-50 text-red-700 border-red-200", icon: Siren },
  resolved:  { label: "Erledigt",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCheck },
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fmtDateShort = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
};

export default function WeeklyControlPage() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/weekly-control/overview");
      setData(data);
      const { data: t } = await api.get("/weekly-control/trend");
      setTrend((t.snapshots || []).map((s) => ({ ...s, label: fmtDateShort(s.date) })));
      const { data: h } = await api.get("/weekly-control/reminders-history");
      setHistory(h.reminders || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/weekly-control/send-reminders");
      if (data.coaches_notified > 0) {
        toast.success(`${data.coaches_notified} Coach(es) benachrichtigt — Frist 24h`);
      } else {
        toast.info("Keine Coaches mit offenen Punkten zu benachrichtigen");
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSending(false); }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get("/weekly-control/pdf", { responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Wochenkontrolle_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setPdfLoading(false); }
  };

  const clients = data?.clients || [];
  const totalIssues = clients.reduce((s, c) => s + c.issues.length, 0);

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1300px] mx-auto" data-testid="weekly-control-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 border-b border-slate-200 pb-8 gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
            <ClipboardCheck size={13} strokeWidth={1.5} /> Management
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">Wochenkontrolle</h1>
          <p className="text-sm text-slate-500 mt-3 max-w-xl">
            Nur AVGS-Kunden mit offenen Punkten (Zielvereinbarung, Anwesenheits-Unterschrift,
            Coaching-Dokumentation, Zeitübersicht) — immer live berechnet.
          </p>
          {data?.generated_at && (
            <p className="text-[11px] text-slate-400 mt-1" data-testid="weekly-control-generated-at">
              Stand: {fmtDateTime(data.generated_at)}
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap w-full sm:w-auto">
          <button onClick={() => setShowHistory((v) => !v)} data-testid="weekly-control-toggle-history"
            className="px-4 py-3 border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-100 flex items-center justify-center gap-2 flex-1 sm:flex-none">
            <History size={14} strokeWidth={1.5} /> {showHistory ? "Historie ausblenden" : "Erinnerungs-Historie"}
          </button>
          <button onClick={load} data-testid="weekly-control-refresh"
            className="px-4 py-3 border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-100 flex items-center justify-center gap-2 flex-1 sm:flex-none">
            <RefreshCw size={14} strokeWidth={1.5} /> Aktualisieren
          </button>
          <button onClick={downloadPdf} disabled={pdfLoading} data-testid="weekly-control-download-pdf"
            className="px-4 py-3 border border-slate-900 text-slate-900 text-sm font-medium hover:bg-slate-900 hover:text-white flex items-center justify-center gap-2 disabled:opacity-50 flex-1 sm:flex-none">
            <FileDown size={14} strokeWidth={1.5} /> {pdfLoading ? "Erzeuge PDF…" : "Als PDF herunterladen"}
          </button>
          <button onClick={sendReminders} disabled={sending || totalIssues === 0} data-testid="weekly-control-send-reminders"
            className="px-6 py-3 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center justify-center gap-2 disabled:opacity-50 w-full sm:w-auto">
            <Send size={14} strokeWidth={1.5} /> {sending ? "Sende…" : "Alle Coaches benachrichtigen"}
          </button>
        </div>
      </div>

      {trend.length >= 2 && (
        <div className="border border-slate-200 bg-white p-6 mb-6" data-testid="weekly-control-trend">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown size={15} strokeWidth={1.5} className="text-slate-400" />
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500">
              Entwicklung offener Punkte
            </div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={trend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip formatter={(v) => [`${v} offen`, ""]} labelFormatter={(l) => `Stand: ${l}`} />
              <Line type="monotone" dataKey="total_issues" stroke="#0F172A" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {showHistory && (
        <div className="border border-slate-200 bg-white p-6 mb-6" data-testid="weekly-control-history">
          <div className="flex items-center gap-2 mb-4">
            <History size={15} strokeWidth={1.5} className="text-slate-400" />
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500">
              Erinnerungs-Historie ({history.length})
            </div>
          </div>
          {history.length === 0 ? (
            <div className="text-sm text-slate-500 italic">Noch keine Erinnerungen versendet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="weekly-control-history-table">
                <thead>
                  <tr className="text-left text-[10px] font-bold uppercase tracking-overline text-slate-400 border-b border-slate-200">
                    <th className="py-2 pr-4">Gesendet</th>
                    <th className="py-2 pr-4">Coach</th>
                    <th className="py-2 pr-4">Kunde</th>
                    <th className="py-2 pr-4">Kategorie</th>
                    <th className="py-2 pr-4">Detail</th>
                    <th className="py-2 pr-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => {
                    const st = STATUS_STYLES[h.status] || STATUS_STYLES.pending;
                    const StIcon = st.icon;
                    return (
                      <tr key={h.reminder_id} className="border-b border-slate-100" data-testid={`weekly-control-history-row-${h.reminder_id}`}>
                        <td className="py-2 pr-4 whitespace-nowrap text-slate-500 font-mono text-xs">{fmtDateTime(h.sent_at)}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{h.coach_name || "—"}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">{h.client_name || "—"}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">
                          <span className={`text-[10px] font-bold uppercase tracking-overline px-2 py-1 border ${CATEGORY_STYLES[h.category] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                            {h.category_label}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-slate-600 max-w-md">{h.detail}</td>
                        <td className="py-2 pr-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-overline px-2 py-1 border ${st.cls}`}>
                            <StIcon size={10} strokeWidth={1.5} /> {st.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-slate-500 text-sm">Lädt…</div>
      ) : clients.length === 0 ? (
        <div className="border border-emerald-200 bg-emerald-50 p-8 flex items-center gap-3" data-testid="weekly-control-empty">
          <CheckCircle2 size={22} strokeWidth={1.5} className="text-emerald-600 shrink-0" />
          <div>
            <div className="font-medium text-emerald-800">Keine offenen Punkte</div>
            <div className="text-sm text-emerald-700">Alle laufenden AVGS-Kunden sind vollständig.</div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {clients.map((c) => (
            <div key={c.client_id} className="border border-slate-200 bg-white p-5" data-testid={`weekly-control-client-${c.client_id}`}>
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={15} strokeWidth={1.5} className="text-amber-500 shrink-0" />
                <div className="font-heading text-lg font-light tracking-tighter text-slate-900">{c.client_name}</div>
                <span className="text-[10px] font-bold uppercase tracking-overline text-slate-400">{c.issues.length} offen</span>
              </div>
              <ul className="space-y-2">
                {c.issues.map((i, idx) => (
                  <li key={idx} className="flex flex-col sm:flex-row sm:items-start gap-1.5 sm:gap-3 text-sm" data-testid={`weekly-control-issue-${c.client_id}-${idx}`}>
                    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-overline px-2 py-1 border w-fit ${CATEGORY_STYLES[i.category] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {i.category_label}
                    </span>
                    <span className="text-slate-700 flex-1 break-words">{i.detail}</span>
                    <span className="text-xs text-slate-400 shrink-0">Coach: {i.coach_name || "—"}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
